import { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client/react';
import * as R from 'ramda';

import textConstants from '@atom/constants/textConstants';
import {
  GET_BASIC_SCHEMAS,
  SCHEMA_CREATE,
  SCHEMA_DELETE,
  SCHEMA_DUPLICATE,
} from '@atom/graph/schema';
import { SearchParamType, useNavigation } from '@atom/hooks/useNavigation';
import { Modal, Progress, Snackbar } from '@atom/mui';
import colors from '@atom/styles/colors';
import { Schema, SchemaSavePayload } from '@atom/types/schema';
import { isNilOrEmpty } from '@atom/utilities/validationUtilities';

import SchemaPortalHeader from './SchemaPortalHeader';
import SchemaPortalTable from './SchemaPortalTable';

import classes from './schemaPortal.module.css';

const RESULTS_PER_PAGE_OPTIONS = [25, 50, 100, 250];

const SEARCH_DEFAULTS = {
  page: 1,
  limit: RESULTS_PER_PAGE_OPTIONS[0],
  sortBy: 'name',
  isAscending: 'asc',
};

const styles = {
  progress: {
    height: '100%',
  },
};

const schemaTileStyle = {
  deleteModalConfirmButton: {
    backgroundColor: colors.brand.red,
  },
};

const SchemaPortal = () => {
  const [
    { page, limit, sortBy, isAscending },
    { navigate, updateSearchParams },
  ] = useNavigation({
    page: SearchParamType.NUMBER,
    limit: SearchParamType.NUMBER,
    sortBy: SearchParamType.STRING,
    isAscending: SearchParamType.STRING,
  });

  const [schemaToDelete, setSchemaToDelete] = useState<Partial<Schema>>(null);

  useEffect(() => {
    if (R.isNil(page)) {
      updateSearchParams({
        page: SEARCH_DEFAULTS.page,
        limit: SEARCH_DEFAULTS.limit,
        sortBy: SEARCH_DEFAULTS.sortBy,
        isAscending: SEARCH_DEFAULTS.isAscending,
      });
    }
  }, []);

  const {
    data: schemaData,
    loading: isLoadingGetSchemas,
    refetch: refetchSchemas,
  } = useQuery(GET_BASIC_SCHEMAS, {
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    variables: {
      input: {
        rootSchemas: true,
        page: page || SEARCH_DEFAULTS.page,
        limit: limit || SEARCH_DEFAULTS.limit,
        sortBy: sortBy || SEARCH_DEFAULTS.sortBy,
        isAscending: isAscending ? isAscending === 'asc' : true,
      },
    },
  });

  const [createSchema] = useMutation(SCHEMA_CREATE);

  const [duplicateSchema, { loading: isDuplicateLoading }] =
    useMutation(SCHEMA_DUPLICATE);

  const [deleteSchema, { loading: isDeleteLoading }] =
    useMutation(SCHEMA_DELETE);

  const handleAddSchema = async (payload: SchemaSavePayload) => {
    try {
      const { data } = await createSchema({
        variables: {
          input: {
            name: payload?.name,
            assetType: payload?.name,
            locationType: payload?.locationType,
            colorId: payload?.colorId,
            markerId: payload?.markerId,
            isMaterial: payload?.isMaterial,
            isPublished: false,
            ...(payload?.budget && { budget: payload.budget }),
          },
        },
      });

      navigate(`/inventoryTypes/${data?.schemaCreate.id}`);
    } catch (error) {
      Snackbar.error({
        message: textConstants.GENERIC_APPLICATION_ERROR_TEXT,
      });
    }
  };

  const handleDuplicateSchema = async (id: string) => {
    try {
      const { data } = await duplicateSchema({
        variables: {
          id,
        },
      });

      navigate(`/inventoryTypes/${data?.schemaDuplicate?.id}`);
    } catch (error) {
      Snackbar.error({
        message: textConstants.GENERIC_APPLICATION_ERROR_TEXT,
      });
    }
  };

  const handleDeleteSchema = async (id: string) => {
    try {
      await deleteSchema({
        variables: {
          id,
        },
      });

      Snackbar.info({
        message: 'Successfully deleted inventory type.',
      });

      refetchSchemas();
    } catch (error) {
      Snackbar.error({
        message: textConstants.GENERIC_APPLICATION_ERROR_TEXT,
      });
    }
  };

  const isTableLoading =
    isLoadingGetSchemas || isDuplicateLoading || isDeleteLoading;

  return (
    <div className={classes.portalWrapper}>
      <SchemaPortalHeader addSchema={handleAddSchema} />
      <div className={classes.portalContent}>
        <div className={classes.leftPaneContainer} />
        <div className={classes.rightPaneContainer}>
          {isTableLoading ? (
            <Progress style={styles.progress} />
          ) : (
            <SchemaPortalTable
              schemas={schemaData?.basicSchemas?.basicSchemas}
              schemaCount={schemaData?.basicSchemas?.totalCount}
              handleDuplicateSchema={handleDuplicateSchema}
              setSchemaToDelete={setSchemaToDelete}
              resultsPerPageOptions={RESULTS_PER_PAGE_OPTIONS}
            />
          )}
        </div>
      </div>
      <Modal
        open={!isNilOrEmpty(schemaToDelete)}
        disabled={isDeleteLoading}
        title={`Delete ${schemaToDelete?.name} Inventory Type?`}
        onCancel={() => setSchemaToDelete(null)}
        confirmButtonText="Delete"
        onConfirm={(): void => {
          handleDeleteSchema(schemaToDelete?.id);
          setSchemaToDelete(null);
        }}
        ConfirmButtonProps={{
          sx: schemaTileStyle.deleteModalConfirmButton,
        }}
      >
        Deleting this inventory type will also delete all items under it and all
        associated assets.
      </Modal>
    </div>
  );
};

export default SchemaPortal;
